import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal, computed } from '@angular/core';
import { environment } from '../../../environments/environment';
import { catchError, lastValueFrom, map, Observable, of, tap } from 'rxjs';
import { TokenService } from './token.service';

interface User {
  id: number;
  dni: string;
  lastName: string;
  name: string;
  phone: string;
  photoUrl: string;
  role: {
    id: number;
    name: string;
  };
  username: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  #httpClient = inject(HttpClient);
  #tokenService = inject(TokenService);
  #endPointBase = `${environment.url}/api`;

  // Signals para los datos del usuario
  user = signal<User | null>(null);
  userRole = signal<string>('');
  isAuthenticated = computed(() => !!this.user());

  constructor() {
    this.loadUserData();
  }

  async login(data: any): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.#httpClient.post<any>(this.#endPointBase + '/login', data).pipe(
          tap((response: any) => {
            if (response?.data.access_token) {
              this.#tokenService.saveToken(response.data.access_token);
            }
          })
        )
      );

      await this.loadUserData();
      return response;
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  }

  getToken(): string | null {
    return this.#tokenService.getToken();
  }

  /**
   * Carga `data-me` una sola vez aunque varios lugares lo pidan a la vez
   * (constructor del servicio, navbar, login, páginas que necesitan el id).
   * Las llamadas concurrentes comparten la misma petición en curso.
   */
  loadUserData(): Promise<void> {
    if (!this.isLoggedIn()) return Promise.resolve();
    this.#userRequest ??= this.#fetchUser().finally(() => (this.#userRequest = null));
    return this.#userRequest;
  }
  #userRequest: Promise<void> | null = null;

  async #fetchUser(): Promise<void> {
    const storedRole = localStorage.getItem('userRole');
    if (storedRole) {
      this.userRole.set(storedRole);
    }

    try {
      const userData: any = await this.getUser();
      this.user.set(userData.data);
      const role = userData.data?.role.name;
      if (role) {
        this.userRole.set(role);
        localStorage.setItem('userRole', role);
      }
    } catch (error) {
      console.error('Error al cargar datos del usuario:', error);
    }
  }

  /**
   * Id del usuario autenticado sin repetir `data-me`: usa el usuario ya
   * cargado y, si aún no llegó, espera la misma petición en curso.
   */
  async currentUserId(): Promise<number> {
    if (!this.user()) await this.loadUserData();
    return this.user()?.id ?? 0;
  }

  async getUser() {
    return lastValueFrom(
      this.#httpClient.get(this.#endPointBase + '/admin/v1/data-me')
    );
  }

  // Verifica si el usuario está autenticado
  isLoggedIn(): boolean {
    const token = this.#tokenService.getToken();
    return !!token;
  }

  // Verifica si el usuario tiene alguno de los roles especificados
  hasRole(allowedRoles: string[]): boolean {
    const currentRole = this.userRole();
    return allowedRoles.includes(currentRole);
  }

  // Elimina el token para cerrar sesión
  logout(): void {
    this.#tokenService.removeToken();

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister().then((success) => {
            console.log('✅ Service Worker eliminado:', success);
          });
        });
      });
    }

    if (typeof window !== 'undefined') {
      localStorage.clear();
    }

    this.user.set(null);
    this.userRole.set('');
  }
}
// import { HttpClient } from '@angular/common/http';
// import { inject, Injectable, signal, computed } from '@angular/core';
// import { environment } from '../../../environments/environment';
// import { catchError, lastValueFrom, map, Observable, of, tap } from 'rxjs';

// interface User {
//   id?: number;
//   name?: string;
//   email?: string;
//   role?: string;
//   // Agrega más propiedades según tu modelo de usuario
// }

// @Injectable({
//   providedIn: 'root',
// })
// export class AuthService {
//   #httpClient = inject(HttpClient);
//   #endPointBase = `${environment.url}/api`;
//   #TOKEN_KEY = 'authToken';

//   // Signals para los datos del usuario
//   user = signal<User | null>(null);
//   userRole = signal<string>('');
//   isAuthenticated = computed(() => !!this.user());

//   constructor() {
//     // Intenta cargar los datos del usuario al iniciar el servicio
//     this.loadUserData();
//   }

//   async login(data: any): Promise<any> {
//     try {
//       const response = await lastValueFrom(
//         this.#httpClient.post<any>(this.#endPointBase + '/login', data).pipe(
//           tap((response: any) => {
//             console.log(response);
//             if (response?.data.access_token) {
//               this.saveToken(response.data.access_token);
//               console.log('Token guardado');
//             }
//           })
//         )
//       );

//       // Después de iniciar sesión, carga los datos del usuario
//       await this.loadUserData();
//       return response;
//     } catch (error) {
//       console.error('Error en login:', error);
//       throw error;
//     }
//   }

//   saveToken(token: string): void {
//     localStorage.setItem(this.#TOKEN_KEY, token);
//   }

//   // Obtiene el token
//   getToken(): string | null {
//     if (typeof window !== 'undefined') {
//       return localStorage.getItem(this.#TOKEN_KEY);
//     }
//     return null;
//   }

//   async loadUserData(): Promise<void> {
//     if (this.isLoggedIn()) {
//       try {
//         const userData: any = await this.getUser();

//         this.user.set(userData.data);
//         this.userRole.set(userData.data.role.id || '');
//       } catch (error) {
//         console.error('Error al cargar datos del usuario:', error);
//         // Si hay error al cargar los datos, posiblemente el token es inválido
//         // this.logout();
//       }
//     }
//   }

//   async getUser() {
//     return lastValueFrom(
//       this.#httpClient.get(this.#endPointBase + '/admin/v1/data-me')
//     );
//   }

//   // Verifica si el usuario está autenticado
//   isLoggedIn(): boolean {
//     const token = this.getToken();
//     return !!token; // Puedes extender esta lógica para verificar la validez del token
//   }

//   // Verifica si el usuario tiene alguno de los roles especificados
//   hasRole(allowedRoles: string[]): boolean {
//     const currentRole = this.userRole();
//     return allowedRoles.includes(currentRole);
//   }

//   // Elimina el token para cerrar sesión
//   logout(): void {
//     if (typeof window !== 'undefined') {
//       localStorage.removeItem(this.#TOKEN_KEY);
//       localStorage.clear();
//       this.user.set(null);
//       this.userRole.set('');
//     }
//   }
// }
